import "rc-slider/assets/index.css";

import { IInferenceModel, SubscriptionTiers, useApp, useGenerationPresets, useTranslation } from "@next-platform/client-common-core";
import {
  CapabilityGate,
  SubCapabilityKey,
  useApplicationCapabilitiesAllowed,
} from "@next-platform/client-common-core/infra/application/capabilities";
import {
  Badge,
  BadgeVariant,
  Button,
  ButtonSize,
  ButtonVariant,
  IconVariants,
  Modal,
  ModalSize,
  Text,
  TextVariants,
} from "@next-platform/client-common-spicy-ui";
import { useState } from "react";

import { Col, Row } from "@/components/customComponents";
import { LocalizedLink } from "@/components/LocalizedLink/LocalizedLink";
import Trans from "@/components/Trans/Trans";
import { EXTERNAL_ROUTES_FOR_APPLICATION, FEATURE_FLAGS, ROUTES } from "@/constants";
import { useFeatureFlag } from "@/hooks";
import { useOverflow } from "@/hooks/useOverflow";
import CapabilityModalConversationImageSettings from "@/pages/Chat/components/CapabilityModalConversationImageSettings";
import ConversationImageSettingsButton from "@/pages/Chat/components/ModalGenerationSettings/components/ConversationImageSettingsButton";
import { InferenceCard } from "@/pages/Chat/components/ModalGenerationSettings/components/InferenceCard";
import { PresetsManager } from "@/pages/Chat/components/ModalGenerationSettings/PresetsManager";
import { ModalGenerationSettingsProps } from "@/pages/Chat/components/ModalGenerationSettings/types";
import { ModalSelectModel } from "@/pages/Chat/components/ModalSelectModel";
import ModelCard from "@/pages/Chat/components/ModalSelectModel/components/ModelCard";
import { useGenerationSettings } from "@/pages/Chat/hooks/useGenerationSettings";
import { cn } from "@/utils";

export default function ModalGenerationSettings({ isOpen, onClose, dataTestId = "" }: ModalGenerationSettingsProps) {
  const { t } = useTranslation();
  const { ref: actionsContainerRef, isOverflowing: isActionsOverflowing } = useOverflow();
  const [isImageSettingsOpen, setIsImageSettingsOpen] = useState(false);
  const [isLocalModelSelectOpen, setIsLocalModelSelectOpen] = useState(false);
  const [stateIsShowUnsavedChangesWarning, setStateIsShowUnsavedChangesWarning] = useState(false);

  const {
    inferenceModel: stagedInferenceModel,
    inferenceMaxNewTokens,
    inferenceTemperature,
    inferenceTopP,
    inferenceTopK,
    updateInferenceModel,
    updateInferenceMaxNewTokens,
    updateInferenceTemperature,
    updateInferenceTopP,
    updateInferenceTopK,
    applyInferenceSettings,
    resetToDefault,
    handleSave,
    settingsAllowed,
    availableMemory,
    contextMemory,
    applicationId,
    isSaveDisabled,
  } = useGenerationSettings();
  const { inferenceModels } = useApp();

  // Get model data based on staged model ID, not Redux state
  const stagedInferenceModelData = inferenceModels?.find((model: IInferenceModel) => model.id === stagedInferenceModel);

  const { hasFeatureFlag: hasPresetsFeature } = useFeatureFlag(FEATURE_FLAGS.GENERATION_PRESETS);
  const { getPresetSettings } = useGenerationPresets();
  const generationSettingsDocUrl = `${EXTERNAL_ROUTES_FOR_APPLICATION[applicationId as keyof typeof EXTERNAL_ROUTES_FOR_APPLICATION]?.docs}/advanced/generation-settings`;
  const isAdvancedSettingsAllowed = useApplicationCapabilitiesAllowed(SubCapabilityKey.ChatGenerationSettingsAdvanced);

  const handleApplyPreset = (presetId: string) => {
    const settings = getPresetSettings(presetId);
    if (!settings) return;

    applyInferenceSettings(settings);
  };

  const handleClose = (open: boolean) => {
    // Only warn if there are actual differences from Redux state (not just interaction)
    if (!open && !isSaveDisabled) {
      setStateIsShowUnsavedChangesWarning(true);
    } else {
      onClose(open);
    }
  };

  const handleDiscardChanges = () => {
    setStateIsShowUnsavedChangesWarning(false);
    onClose(false);
  };

  const handleKeepEditing = () => {
    setStateIsShowUnsavedChangesWarning(false);
  };

  return (
    <>
      <Modal
        title={t("chat:modal.generationSettings.title")}
        open={!!(isOpen && !isImageSettingsOpen && !isLocalModelSelectOpen && !stateIsShowUnsavedChangesWarning)}
        onOpenChange={handleClose}
        className="dark:!bg-gray-6 gap-md"
        size={ModalSize.LARGE}
        actions={
          settingsAllowed ? (
            <div
              ref={actionsContainerRef}
              className={cn("flex justify-between gap-sm", isActionsOverflowing && "flex-col-reverse")}
              data-testid={dataTestId}
            >
              <Button
                onClick={resetToDefault}
                className={cn("relative px-md transition-colors", !isActionsOverflowing && "-left-md mr-md")}
                size={ButtonSize.LARGE}
                variant={ButtonVariant.TEXT}
              >
                <Trans i18nKey="common:reset" />
              </Button>
              <Row className={cn("gap-sm", isActionsOverflowing && "flex-col-reverse")}>
                <ConversationImageSettingsButton onPress={() => setIsImageSettingsOpen(true)} />
                <Button size={ButtonSize.LARGE} onClick={() => handleSave(onClose)} disabled={isSaveDisabled}>
                  <Trans i18nKey="chat:modal.generationSettings.cta.apply" />
                </Button>
              </Row>
            </div>
          ) : (
            <Col className="gap-lg" dataTestId={dataTestId}>
              <Text variant={TextVariants.LABEL_SMALL_REGULAR} className="text-gray-11">
                <Trans
                  i18nKey="chat:modal.generationSettings.availableOnPlans"
                  values={{ basicTier: SubscriptionTiers.BASIC, premiumTier: SubscriptionTiers.PREMIUM }}
                  components={{
                    basicLink: <LocalizedLink to={ROUTES.subscribe} className="underline font-bold" />,
                    premiumLink: <LocalizedLink to={ROUTES.subscribe} className="underline font-bold" />,
                  }}
                />
              </Text>
              <LocalizedLink to={ROUTES.subscribe}>
                <Button
                  variant={ButtonVariant.TRANSPARENT}
                  size={ButtonSize.LARGE}
                  iconStart={IconVariants.CROWN}
                  className="bg-gradient-to-r from-purple-9 to-blue-9 gap-lg w-full text-white"
                >
                  <Trans i18nKey="common:upgradeNow" />
                </Button>
              </LocalizedLink>
              <Text variant={TextVariants.LABEL_SMALL_REGULAR} className="text-gray-12">
                <Trans
                  i18nKey="chat:modal.generationSettings.settingsNotAllowed.moreDetails"
                  components={{
                    generationSettingsLink: <LocalizedLink target="_blank" to={generationSettingsDocUrl} className="underline" />,
                  }}
                />
              </Text>
            </Col>
          )
        }
      >
        <Text variant={TextVariants.LABEL_SMALL_REGULAR} className="text-gray-11">
          <Trans i18nKey="chat:modal.generationSettings.description" />
        </Text>
        <Col>
          <Row className="gap-1 items-center">
            <Text variant={TextVariants.LABEL_LARGE_MEDIUM} className="text-gray-12">
              <Trans i18nKey="chat:modal.generationSettings.contextMemory.label" />
            </Text>
            <Badge variant={BadgeVariant.OUTLINED} showDot={false}>
              {contextMemory}
            </Badge>
          </Row>
          <Text variant={TextVariants.LABEL_SMALL_REGULAR} className="text-gray-11">
            <Trans i18nKey="chat:modal.generationSettings.contextMemory.description" values={{ availableMemory }} />
          </Text>
        </Col>

        <CapabilityGate capability={SubCapabilityKey.ChatOpenaiModel}>
          <Col className="items-start" dataTestId="ChatOpenaiModelCapabilityGate">
            <Text variant={TextVariants.LABEL_LARGE_MEDIUM} className="text-gray-12">
              <Trans i18nKey="chat:modal.generationSettings.inferenceModel.label" />
            </Text>
            {stagedInferenceModelData ? <ModelCard model={stagedInferenceModelData} readOnly /> : null}
            <Button variant={ButtonVariant.OUTLINED} onClick={() => setIsLocalModelSelectOpen(true)}>
              <Trans i18nKey="chat:modal.generationSettings.cta.changeModel" />
            </Button>
          </Col>
        </CapabilityGate>

        {hasPresetsFeature ? (
          <PresetsManager
            data-testid="GenerationPresetsManager"
            currentSettings={{
              modelId: stagedInferenceModel || "default",
              maxNewTokens: Number(inferenceMaxNewTokens),
              temperature: Number(inferenceTemperature),
              topP: Number(inferenceTopP),
              topK: Number(inferenceTopK),
            }}
            modelName={stagedInferenceModelData?.name || t("chat:modal.generationSettings.unknownModel")}
            onApplyPreset={handleApplyPreset}
            isDisabled={!settingsAllowed}
          />
        ) : null}

        {isAdvancedSettingsAllowed ? (
          <div data-testid="AdvancedSettingsSection">
            <InferenceCard
              minValue={180}
              maxValue={300}
              value={inferenceMaxNewTokens}
              onChange={updateInferenceMaxNewTokens}
              isDisabled={!settingsAllowed}
              infoLink={`${EXTERNAL_ROUTES_FOR_APPLICATION[applicationId as keyof typeof EXTERNAL_ROUTES_FOR_APPLICATION]?.docs}/advanced/model-settings#response-max-tokens`}
              label={t("chat:modal.generationSettings.responseMaxTokens.label")}
              description={t("chat:modal.generationSettings.responseMaxTokens.description")}
              area_label="response-max-tokens-info"
              step={1}
            />

            <InferenceCard
              minValue={0}
              maxValue={1.5}
              value={inferenceTemperature}
              onChange={updateInferenceTemperature}
              isDisabled={!settingsAllowed}
              infoLink={`${EXTERNAL_ROUTES_FOR_APPLICATION[applicationId as keyof typeof EXTERNAL_ROUTES_FOR_APPLICATION]?.docs}/advanced/model-settings#temperature`}
              label={t("chat:modal.generationSettings.temperature.label")}
              description={t("chat:modal.generationSettings.temperature.description")}
              area_label="temperature-info"
              step={0.01}
            />

            <InferenceCard
              minValue={0.01}
              maxValue={1}
              value={inferenceTopP}
              onChange={updateInferenceTopP}
              isDisabled={!settingsAllowed}
              infoLink={`${EXTERNAL_ROUTES_FOR_APPLICATION[applicationId as keyof typeof EXTERNAL_ROUTES_FOR_APPLICATION]?.docs}/advanced/model-settings#top-p-nucleus-sampling`}
              label={t("chat:modal.generationSettings.topP.label")}
              description={t("chat:modal.generationSettings.topP.description")}
              area_label="top-p-info"
              step={0.01}
            />

            <InferenceCard
              minValue={1}
              maxValue={100}
              value={inferenceTopK}
              onChange={updateInferenceTopK}
              isDisabled={!settingsAllowed}
              infoLink={`${EXTERNAL_ROUTES_FOR_APPLICATION[applicationId as keyof typeof EXTERNAL_ROUTES_FOR_APPLICATION]?.docs}/advanced/model-settings#top-k`}
              label={t("chat:modal.generationSettings.topK.label")}
              description={t("chat:modal.generationSettings.topK.description")}
              area_label="top-k-info"
              step={1}
            />
          </div>
        ) : null}
      </Modal>

      <Modal
        title={t("chat:modal.generationSettings.unsavedChanges.title")}
        open={stateIsShowUnsavedChangesWarning}
        onOpenChange={(open: boolean) => !open && handleKeepEditing()}
        className="dark:!bg-gray-6"
        size={ModalSize.SMALL}
        classNames={{
          actions: "flex-wrap",
        }}
        actions={[
          {
            label: t("chat:modal.generationSettings.unsavedChanges.keepEditing"),
            variant: ButtonVariant.OUTLINED,
            onPress: handleKeepEditing,
            size: ButtonSize.LARGE,
          },
          {
            label: t("chat:modal.generationSettings.unsavedChanges.discardChanges"),
            variant: ButtonVariant.TRANSPARENT,
            onPress: handleDiscardChanges,
            size: ButtonSize.LARGE,
          },
        ]}
      >
        <Text variant={TextVariants.LABEL_MEDIUM_REGULAR} className="text-gray-11">
          <Trans i18nKey="chat:modal.generationSettings.unsavedChanges.message" />
        </Text>
      </Modal>

      <CapabilityModalConversationImageSettings isOpen={isImageSettingsOpen} setIsOpen={setIsImageSettingsOpen} />
      <CapabilityGate capability={SubCapabilityKey.ChatOpenaiModel}>
        <ModalSelectModel
          dataTestId="ChatOpenaiModelSelectModelCapabilityGate"
          isOpen={isLocalModelSelectOpen}
          setIsOpen={setIsLocalModelSelectOpen}
          onModelChange={updateInferenceModel}
          currentModelId={stagedInferenceModel}
        />
      </CapabilityGate>
    </>
  );
}
